import { SourceLinks } from '@/components/ui/source-links';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatFullRefAsLink } from '@/lib/utils/reference-utils';

interface AllusionCardProps {
  allusion: {
    ref?: string;
    type: string;
    sourceRef: string;
    text?: string;
    explanation?: string;
    importance?: Importance;
    sources?: Source[];
  };
  isDarkMode: boolean;
  setUrl: (url: string) => void;
}

/**
 * Helper function to get tooltip text for allusion type
 */
function getAllusionTypeTooltip(type: string): string {
  switch (type) {
    case 'Direct Quote':
      return 'An explicit, formal reference to an earlier text that is cited or closely paraphrased';
    case 'Allusion':
      return 'A non-formal invocation of an earlier text that the author could reasonably have been expected to know';
    case 'Echo':
      return 'Resonations of Scriptural texts that readers might hear, and which are not necessarily conscious or explicit references';
    case 'Typology':
      return 'Type-antitype relationship (OT type fulfilled in NT, or OT type pointing forward)';
    case 'Reference':
      return 'General reference to a concept in an earlier text';
    case 'Prophecy':
      return 'Explicit prediction of future events';
    case 'Foreshadowing':
      return 'Narrative anticipation of later events';
    case 'Fulfillment':
      return 'Explicit programmatic fulfillment of an earlier text';
    case 'Canonical Connection':
      return 'Intertextual connection between biblical passages';
    default:
      return type;
  }
}

/**
 * Shared component for displaying a single allusion/reference
 * Used in both the scriptural context page and the capability modal
 */
export function AllusionCard({ allusion, isDarkMode, setUrl }: AllusionCardProps) {
  return (
    <div className="text-sm">
      {/* Main content with rounded border grid */}
      <div className="border border-muted rounded-md overflow-hidden">
        <div className="grid grid-cols-[auto_auto_auto_1fr]">
          {/* Column 1: allusion.ref (passage location) */}
          {allusion.ref && (
            <div className="text-xs text-muted-foreground whitespace-nowrap px-3 py-2 bg-muted/30">
              {allusion.ref}
            </div>
          )}

          {/* Column 2: allusion.type */}
          <div className="text-xs px-3 py-2 border-l border-muted bg-muted/30">
            <div className="flex items-center gap-1.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span className="cursor-help">{allusion.type}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{getAllusionTypeTooltip(allusion.type)}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Column 3: allusion.sourceRef (clickable) */}
          <div className="text-xs px-3 py-2 border-l border-muted bg-muted/30 max-w-[120px] sm:max-w-none sm:whitespace-nowrap break-words">
            {formatFullRefAsLink(allusion.sourceRef, isDarkMode, setUrl)}
          </div>

          {/* Column 4: allusion.text */}
          <div className="text-sm px-3 py-2 border-l border-muted">
            {allusion.text ? (
              <p className="text-muted-foreground italic">"{allusion.text}"</p>
            ) : (
              <p className="text-muted-foreground">—</p>
            )}
          </div>
        </div>

        {/* Explanation row (full width) */}
        {allusion.explanation && (
          <div className="border-t border-muted px-3 py-2 bg-muted/10">
            <p className="text-xs">{allusion.explanation}</p>
          </div>
        )}

        {/* Sources row (full width) */}
        {allusion.sources && allusion.sources.length > 0 && (
          <div className="border-t border-muted px-3 py-2 bg-muted/10">
            <SourceLinks sources={allusion.sources} />
          </div>
        )}
      </div>
    </div>
  );
}

interface AllusionsListProps {
  allusions: AllusionCardProps['allusion'][];
  isDarkMode: boolean;
  setUrl: (url: string) => void;
}

/**
 * List wrapper for multiple allusions
 */
export function AllusionsList({ allusions, isDarkMode, setUrl }: AllusionsListProps) {
  if (!allusions || allusions.length === 0) return null;

  return (
    <div className="space-y-3">
      {allusions.map((allusion, index) => (
        <AllusionCard
          key={index}
          allusion={allusion}
          isDarkMode={isDarkMode}
          setUrl={setUrl}
        />
      ))}
    </div>
  );
}

